import axios from "axios";
import { getAPIUrl, getAuthAPIUrl } from "./apiUtils";

export const AUTH_EXPIRED_EVENT = "auth:expired";

const PUBLIC_SUFFIXES = [
  "/profile/login",
  "/profile/register",
  "/common/status"
];

const PUBLIC_EXACT_PATHS = [
  "/health",
  "/status",
];

const getToken = () => localStorage.getItem("token");
const AUTH_SESSION_REQUEST_FLAG = "__socioHadAuthSession";
let hasNotifiedAuthExpired = false;

const hasAuthorizationHeader = (headers) => {
  if (!headers) return false;
  if (typeof headers.get === "function") {
    return Boolean(headers.get("Authorization"));
  }
  return Boolean(headers.Authorization || headers.authorization);
};

const hasStoredAuthSession = () =>
  Boolean(getToken() || localStorage.getItem("user"));

const getPathname = (url) => {
  try {
    return new URL(String(url), window.location.origin).pathname;
  } catch (_) {
    return String(url);
  }
};

const isPublicUrl = (url) => {
  if (!url) return false;
  const pathname = getPathname(url);
  return (
    PUBLIC_EXACT_PATHS.includes(pathname) ||
    PUBLIC_SUFFIXES.some((path) => pathname.endsWith(path))
  );
};

const isApiUrl = (url) => {
  if (!url) return false;
  const urlText = String(url);
  return (
    urlText.startsWith(getAPIUrl()) ||
    urlText.startsWith(getAuthAPIUrl()) ||
    urlText.startsWith("/api/")
  );
};

const notifyAuthExpired = () => {
  if (hasNotifiedAuthExpired || typeof window === "undefined") return;
  hasNotifiedAuthExpired = true;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
};

const resetAuthExpiredNotification = () => {
  hasNotifiedAuthExpired = false;
};

export const getAuthHeaders = (headers = {}) => {
  const token = getToken();
  if (!token || headers.Authorization || headers.authorization) return headers;
  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

export const setupAxiosAuthInterceptor = () => {
  axios.interceptors.request.use((config) => {
    if (isApiUrl(config.url) && !isPublicUrl(config.url)) {
      const token = getToken();
      config[AUTH_SESSION_REQUEST_FLAG] =
        hasStoredAuthSession() || hasAuthorizationHeader(config.headers);
      if (token) {
        config.headers = config.headers || {};
        if (typeof config.headers.set === "function") {
          config.headers.set("Authorization", `Bearer ${token}`);
        } else if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (isPublicUrl(response.config?.url) && response.data?.token) {
        resetAuthExpiredNotification();
      }
      return response;
    },
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url;
      if (
        status === 401 &&
        isApiUrl(url) &&
        !isPublicUrl(url) &&
        error?.config?.[AUTH_SESSION_REQUEST_FLAG] === true
      ) {
        notifyAuthExpired();
      }
      return Promise.reject(error);
    }
  );
};

export const apiFetch = async (url, options = {}) => {
  const shouldAuthorize = isApiUrl(url) && !isPublicUrl(url);
  const hadAuthSession = shouldAuthorize && (
    hasStoredAuthSession() || hasAuthorizationHeader(options.headers)
  );
  const headers = shouldAuthorize
    ? getAuthHeaders(options.headers || {})
    : options.headers;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && shouldAuthorize && hadAuthSession) {
    notifyAuthExpired();
  }

  if (isPublicUrl(url)) {
    try {
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();
      if (data?.token) {
        resetAuthExpiredNotification();
      }
    } catch (_) {
      // Non-JSON public responses do not affect auth state.
    }
  }

  return response;
};

setupAxiosAuthInterceptor();
